import { LitElement, html, nothing } from "lit";
import { fa, onceParentAvailable } from "../../../utils/widgets.js";
import {
	faChevronDown, faSliders, faXmark
} from "@fortawesome/free-solid-svg-icons";
import { activeIcon } from "../../styles.js";

/**
 * Collapsible button showing off map filters menus.
 * @class Panoramax.components.ui.widgets.MapFiltersButton
 * @element pnx-widget-mapfilters
 * @extends [lit.LitElement](https://lit.dev/docs/api/LitElement/)
 * @example
 * ```html
 * <pnx-widget-mapfilters user-search="" _parent=${viewer} />
 * ```
 */
export default class MapFiltersButton extends LitElement {
	/** @private */
	static styles = [activeIcon];

	/**
	 * Component properties.
	 * @memberof Panoramax.components.ui.widgets.MapFiltersButton#
	 * @type {Object}
	 * @property {boolean} [user-search=false] Should user search filter show up ?
	 * @property {boolean} [quality-score=false] Should quality score filter show up ?
	 * @property {boolean} [no-date=false] Should date filters be hidden ?
	 * @property {boolean} [no-picture-type=false] Should picture type filter be hidden ?
	 * @property {boolean} [no-gps=false] Should GPS precision filter be hidden ?
	 * @property {boolean} [no-source=false] Should pictures source (world, instance) filter be hidden ?
	 */
	static properties = {
		"quality-score": {type: Boolean},	// Should be available in pnx-map-filters-menu as well
		"user-search": {type: Boolean},		// Should be available in pnx-map-filters-menu as well
		"no-date": {type: Boolean},			// Should be available in pnx-map-filters-menu as well
		"no-picture-typetagged": {type: Boolean},    //MINE
		"no-picture-type": {type: Boolean},	// Should be available in pnx-map-filters-menu as well
		"no-gps": {type: Boolean},			// Should be available in pnx-map-filters-menu as well
		"no-source": {type: Boolean},		// Should be available in pnx-map-filters-menu as well
		_active: {state: true},
	};

	constructor() {
		super();

		// Add them in render below as well
		this["user-search"] = false;
		this["quality-score"] = false;
		this["no-date"] = false;
		this["no-picture-typetagged"] = false;
		this["no-picture-type"] = false;
		this["no-gps"] = false;
		this["no-source"] = false;
		this._active = false;
	}

	/** @private */
	connectedCallback() {
		super.connectedCallback();

		// Listen to user visibility changes to switch the filter active icon
		onceParentAvailable(this).then(() => this._parent?.onceMapReady?.().then(() => {
			this._active = this._isActive();
			this._parent.mapStyleComposer.addEventListener("filters-changed", () => this._active = this._isActive());
			this._parent.mapStyleComposer.addEventListener("panoramax-changed", () => this._active = this._isActive());
		}));
	}

	/** @private */
	_isActive() {
		return Object
			.entries(this._parent.mapStyleComposer.panoramaxFilters)
			.filter(([k,v]) => k && v && !["featuresRestrictions", "theme"].includes(k)).length > 0
			|| this._parent.mapStyleComposer.isPanoramaxEndpointSingleUser();
	}

	/** @private */
	render() {
		const isSmall = this._parent?.isWidthSmall() || this._parent?.isWidthMedium();
		const resetIcon = fa(faXmark, {styles: {"pointer-events": "auto"}});
		resetIcon.addEventListener("click", e => {
			e.stopPropagation();
			this.shadowRoot.querySelector("pnx-map-filters-menu")._onReset();
			this.shadowRoot.querySelector("#pnx-widget-filter")._opened = false;
		});

		return html`
			<pnx-togglable-group
				id="pnx-widget-filter"
				padded="false"
				class="pnx-only-map pnx-print-hidden"
				._parent=${this._parent}
			>
				<pnx-button kind="flat" size="xl" slot="button">
					${fa(faSliders)}
					${this._active ? html`<span class="pnx-active-icon"></span>` : nothing}
					${isSmall ? nothing : this._parent?._t.pnx.filters}
					${isSmall ? nothing : (this._active ? resetIcon : fa(faChevronDown))}
				</pnx-button>
				<pnx-map-filters-menu
					id="pnx-map-filters-menu"
					._parent=${this._parent}
					user-search=${this["user-search"]}
					quality-score=${this["quality-score"]}
					.no-date=${this["no-date"]}
					.no-picture-typetagged=${this["no-picture-typetagged"]}
					.no-picture-type=${this["no-picture-type"]}
					.no-gps=${this["no-gps"]}
					.no-source=${this["no-source"]}
				></pnx-map-filters-menu>
			</pnx-togglable-group>
		`;
	}
}

customElements.define("pnx-widget-mapfilters", MapFiltersButton);
